import { useState, useEffect, useMemo } from 'react'
import { Rss, Save, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { USMap, US_STATES } from '@/components/us-map'
import { USAInteractiveMap } from '@/components/usa-interactive-map'
import { dashboardApi, newsApi, newsletterApi } from '@/services/api'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { motion } from 'framer-motion'

export function DashboardFeed() {
  const queryClient = useQueryClient()

  // State
  const [sortType, setSortType] = useState('alphabetical') // 'alphabetical', 'byState', 'byInterest'
  const [viewType, setViewType] = useState('map') // 'map', 'list', 'grid'
  const [selectedState, setSelectedState] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedFeeds, setSelectedFeeds] = useState([])

  // Fetch subscription and feeds
  const { data: subscription, isLoading: loadingSubscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const response = await dashboardApi.getSubscribedFeeds()
      return response.data
    },
    onError: () => {
      toast.error('Failed to load subscription')
    }
  })

  // Fetch all providers
  const { data: allProviders } = useQuery({
    queryKey: ['allProviders'],
    queryFn: async () => {
      const response = await newsApi.getProviders()
      return response.data
    }
  })

  // Fetch all newsletters
  const { data: allNewsletters } = useQuery({
    queryKey: ['allNewsletters'],
    queryFn: async () => {
      const response = await newsletterApi.list()
      return response.data
    }
  })

  // Fetch displayed newsletters (user preferences)
  const { data: displayNewsletters } = useQuery({
    queryKey: ['displayNewsletters'],
    queryFn: async () => {
      const response = await newsletterApi.getDisplay()
      return response.data
    },
    enabled: !!subscription // Only fetch if authenticated/subscribed
  })

  // Fetch user newsletter subscriptions (for access check)
  const { data: userNewsletterSubscriptions } = useQuery({
    queryKey: ['userNewsletterSubscriptions'],
    queryFn: async () => {
      const response = await newsletterApi.getSubscriptions()
      return response.data
    },
    enabled: !!subscription
  })

  // Initialize selected feeds from subscription AND display newsletters
  useEffect(() => {
    let feeds = []

    // Add standard feeds
    if (subscription?.display_feeds) {
      feeds = [...subscription.display_feeds]
    }

    // Add displayed newsletters
    if (displayNewsletters && allNewsletters) {
      const nlFeeds = displayNewsletters.map(dn => {
        // Find full newsletter object or use minimal info
        const fullNl = allNewsletters.find(n => n.alias === dn.newsletter.alias) || dn.newsletter
        return {
          alias: fullNl.alias,
          display_name: fullNl.display_name || fullNl.name,
          is_newsletter: true,
          non_profit_org: fullNl.has_free_tier,
          basic_feed: false,
          // ... other mapped fields
        }
      })
      feeds = [...feeds, ...nlFeeds]
    }

    // Only update if we have data to avoid wiping state on partial loads
    if (subscription?.display_feeds || displayNewsletters) {
      setSelectedFeeds(feeds)
    }
  }, [subscription, displayNewsletters, allNewsletters])

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (feeds) => dashboardApi.updateDisplayFeeds(feeds),
    onSuccess: () => {
      queryClient.invalidateQueries(['subscription'])
      queryClient.invalidateQueries(['displayNewsletters'])
      toast.success('Feed preferences saved!')
    },
    onError: () => {
      toast.error('Failed to save preferences')
    }
  })

  // Merge Providers and Newsletters
  const allFeeds = useMemo(() => {
    const feeds = [...(allProviders || [])]

    if (allNewsletters) {
      const formattedNewsletters = allNewsletters.map(nl => ({
        ...nl,
        alias: nl.alias, // Ensure alias is present
        display_name: nl.display_name || nl.name,
        is_newsletter: true,
        // Map to tiers: Free Tier -> Non-Profit bucket, Paid -> Premium bucket
        non_profit_org: nl.has_free_tier,
        basic_feed: false,
        state: null // Newsletters typically global
      }))
      feeds.push(...formattedNewsletters)
    }
    return feeds
  }, [allProviders, allNewsletters])

  // Categorize feeds into tiers and newsletters
  const basicFeeds = allFeeds.filter(feed => !feed.is_newsletter && (feed.non_profit_org || feed.basic_feed))
  const premiumFeeds = allFeeds.filter(feed => !feed.is_newsletter && !feed.non_profit_org && !feed.basic_feed)
  const newsletterFeeds = allFeeds.filter(feed => feed.is_newsletter)

  // Get user's plan name
  const userPlan = subscription?.plan?.name || 'Free'

  // Check if user has access to a feed
  const hasAccessToFeed = (feedAlias) => {
    // Find the feed
    const feed = allFeeds.find(f => f.alias === feedAlias)
    if (!feed) return false

    // NEWSLETTER LOGIC
    if (feed.is_newsletter) {
      // Must be subscribed to access/select
      // Check subscription list
      if (!userNewsletterSubscriptions) return false
      return userNewsletterSubscriptions.some(sub => sub.newsletter.alias === feedAlias && sub.is_active)
    }

    // FEED LOGIC
    // Normalize plan name
    const planName = userPlan.toLowerCase()

    // Premium plan gives access to everything
    if (planName.includes('premium')) return true

    // Basic plan gives access to Basic feeds (and Free feeds)
    if (planName.includes('basic')) {
      return feed.non_profit_org || feed.basic_feed
    }

    // Free plan (or unknown) - only non-profit
    return feed.non_profit_org
  }

  // Calculate feed counts per state (only for standard feeds as newsletters are global)
  const feedCounts = {}
  US_STATES.forEach(state => {
    feedCounts[state.id] = allFeeds.filter(feed => !feed.is_newsletter && feed.state === state.id).length
  })

  // Check if feed is selected
  const isFeedSelected = (feedAlias) => {
    return selectedFeeds.some(feed => (feed.alias || feed) === feedAlias)
  }

  // Handle individual feed toggle (SECURITY: Check access before adding)
  const toggleFeed = (feed) => {
    if (isFeedSelected(feed.alias)) {
      setSelectedFeeds(selectedFeeds.filter(f =>
        (f.alias || f) !== feed.alias
      ))
    } else {
      // SECURITY: Only allow selecting feeds user has access to
      if (hasAccessToFeed(feed.alias)) {
        setSelectedFeeds([...selectedFeeds, feed])
      } else {
        if (feed.is_newsletter) {
          toast.error('You need to subscribe to this newsletter first')
        } else {
          toast.error('You need to upgrade your subscription to access this feed')
        }
      }
    }
  }

  // Handle state-level selection (SECURITY: Only select feeds user has access to)
  const toggleStateFeeds = (stateId) => {
    const feedsInState = allFeeds.filter(feed => feed.state === stateId)
    // SECURITY: Only include feeds the user has access to
    const accessibleFeedsInState = feedsInState.filter(feed => hasAccessToFeed(feed.alias))
    const allAccessibleSelected = accessibleFeedsInState.every(feed => isFeedSelected(feed.alias))

    if (allAccessibleSelected) {
      // Deselect all accessible feeds in this state
      setSelectedFeeds(selectedFeeds.filter(selected =>
        !accessibleFeedsInState.some(stateFeed => stateFeed.alias === (selected.alias || selected))
      ))
    } else {
      // Select only accessible feeds in this state that aren't already selected
      const newFeeds = accessibleFeedsInState.filter(feed => !isFeedSelected(feed.alias))
      setSelectedFeeds([...selectedFeeds, ...newFeeds])
    }
  }

  // Check if all accessible feeds in a state are selected
  const isStateSelected = (stateId) => {
    const feedsInState = allFeeds.filter(feed => feed.state === stateId)
    // SECURITY: Only check accessible feeds
    const accessibleFeedsInState = feedsInState.filter(feed => hasAccessToFeed(feed.alias))
    if (accessibleFeedsInState.length === 0) return false
    return accessibleFeedsInState.every(feed => isFeedSelected(feed.alias))
  }

  // Get feeds for selected state
  const getStateFeeds = (stateName) => {
    const state = US_STATES.find(s => s.name === stateName)
    if (!state) return []
    // Only standard feeds have state
    return allFeeds.filter(feed => !feed.is_newsletter && feed.state === state.id)
  }

  // Get state ID by name
  const getStateIdByName = (stateName) => {
    const state = US_STATES.find(s => s.name === stateName)
    return state?.id
  }

  // Sort feeds alphabetically by display_name
  const sortedBasicFeeds = [...basicFeeds].sort((a, b) =>
    (a.display_name || '').localeCompare(b.display_name || '')
  )
  const sortedPremiumFeeds = [...premiumFeeds].sort((a, b) =>
    (a.display_name || '').localeCompare(b.display_name || '')
  )
  const sortedNewsletterFeeds = [...newsletterFeeds].sort((a, b) =>
    (a.display_name || '').localeCompare(b.display_name || '')
  )

  // Group feeds by state (Standard feeds only)
  const groupedByState = US_STATES.map(state => ({
    ...state,
    feeds: allFeeds.filter(feed => !feed.is_newsletter && feed.state === state.id)
  })).filter(state => state.feeds.length > 0)

  // Group feeds by category and subcategory
  const groupedByCategory = useMemo(() => {
    const categories = {}

    allFeeds.forEach(feed => {
      if (feed.is_newsletter) return // Skip newsletters for category grouping

      const categoryTitle = feed.category?.title || 'Uncategorized'
      const categorySlug = feed.category?.slug || 'uncategorized'

      if (!categories[categorySlug]) {
        categories[categorySlug] = {
          title: categoryTitle,
          slug: categorySlug,
          subcategories: {}
        }
      }

      const subcategoryTitle = feed.subcategory?.title || 'General'
      const subcategorySlug = feed.subcategory?.slug || 'general'

      if (!categories[categorySlug].subcategories[subcategorySlug]) {
        categories[categorySlug].subcategories[subcategorySlug] = {
          title: subcategoryTitle,
          slug: subcategorySlug,
          feeds: []
        }
      }

      categories[categorySlug].subcategories[subcategorySlug].feeds.push(feed)
    })

    // Convert to array and sort
    return Object.values(categories).map(cat => ({
      ...cat,
      subcategories: Object.values(cat.subcategories).sort((a, b) =>
        a.title.localeCompare(b.title)
      )
    })).sort((a, b) => a.title.localeCompare(b.title))
  }, [allFeeds])

  // Handle save (SECURITY: Final validation before sending to backend)
  const handleSave = () => {
    // SECURITY: Filter out any feeds user doesn't have access to
    const validFeeds = selectedFeeds.filter(feed => {
      const feedAlias = feed.alias || feed
      return hasAccessToFeed(feedAlias)
    })

    // Backend expects array of objects with 'alias' property
    const feedsToSave = validFeeds.map(feed => ({
      alias: feed.alias || feed
    }))

    // Warn if some feeds were filtered out
    if (validFeeds.length < selectedFeeds.length) {
      toast.warning(`${selectedFeeds.length - validFeeds.length} selections removed (subscription required)`)
      setSelectedFeeds(validFeeds) // Update UI
    }

    saveMutation.mutate(feedsToSave)
  }

  if (loadingSubscription) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <Rss className="w-8 h-8 text-primary" />
          My Feed
        </h1>
        <p className="text-gray-600">
          Customize your news feed by selecting sources and newsletters
        </p>
      </div>

      {/* Stats with Save Button */}
      <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-6 mb-6 border border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-8">
            <div>
              <p className="text-sm text-gray-600 mb-1">Selected Sources</p>
              <p className="text-3xl font-bold text-gray-900">
                {selectedFeeds.length} / {allFeeds.length}
              </p>
            </div>
            {/* Only show state count if we have feeds with states */}
            {groupedByState.length > 0 && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Available States</p>
                <p className="text-3xl font-bold text-gray-900">
                  {Object.values(feedCounts).filter(count => count > 0).length}
                </p>
              </div>
            )}
          </div>
          <Button
            onClick={handleSave}
            disabled={saveMutation.isLoading}
            className="flex items-center gap-2 bg-primary hover:bg-primary"
            size="lg"
          >
            {saveMutation.isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Sorting Options */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4">Sort & Filter</h3>

        <div className="space-y-4">
          {/* Main sorting options */}
          <RadioGroup value={sortType} onValueChange={(value) => {
            setSortType(value)
            setSelectedState(null)
            setSelectedCategory(null)
          }}>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="alphabetical" id="alphabetical" />
                <Label htmlFor="alphabetical" className="text-sm font-medium cursor-pointer">
                  Alphabetical
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <RadioGroupItem value="byState" id="byState" />
                <Label htmlFor="byState" className="text-sm font-medium cursor-pointer">
                  By State
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <RadioGroupItem value="byInterest" id="byInterest" />
                <Label htmlFor="byInterest" className="text-sm font-medium cursor-pointer">
                  By Interest
                </Label>
              </div>
            </div>
          </RadioGroup>

          {/* View type for By State */}
          {sortType === 'byState' && (
            <RadioGroup value={viewType} onValueChange={setViewType} className="ml-6">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="map" id="map" />
                  <Label htmlFor="map" className="text-sm cursor-pointer">
                    Map View
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="grid" id="grid" />
                  <Label htmlFor="grid" className="text-sm cursor-pointer">
                    Grid View
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="list" id="list" />
                  <Label htmlFor="list" className="text-sm cursor-pointer">
                    List View
                  </Label>
                </div>
              </div>
            </RadioGroup>
          )}
        </div>
      </div>

      {/* Alphabetical View */}
      {sortType === 'alphabetical' && (
        <div className="space-y-6">
          {/* Basic Feeds (Free + Basic combined) */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="font-semibold text-gray-900">Basic Feeds</h3>
              <span className="px-2 py-1 bg-slate-100 text-primary text-xs font-medium rounded-full">
                {sortedBasicFeeds.length} Total
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
              {sortedBasicFeeds.map((feed) => {
                const hasAccess = hasAccessToFeed(feed.alias)
                const isFreeFeed = feed.non_profit_org
                return (
                  <motion.div
                    key={feed.alias}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${hasAccess
                      ? 'hover:bg-gray-50 border-gray-200'
                      : 'bg-gray-50 border-gray-300 opacity-60'
                      }`}
                    title={!hasAccess ? 'Upgrade to Basic plan to access' : (feed.short_description || 'News feed')}
                  >
                    <div className="relative">
                      <Checkbox
                        id={feed.alias}
                        checked={isFeedSelected(feed.alias)}
                        onCheckedChange={() => hasAccess && toggleFeed(feed)}
                        disabled={!hasAccess}
                      />
                      {!hasAccess && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="text-orange-600 text-lg font-bold">✖</span>
                        </div>
                      )}
                    </div>
                    <Label
                      htmlFor={feed.alias}
                      className={`flex-1 text-sm ${hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'
                        }`}
                      title={feed.short_description || feed.display_name}
                    >
                      {feed.display_name}
                      {isFreeFeed ? (
                        <span className="ml-2 text-xs text-green-600">(Free)</span>
                      ) : !hasAccess && (
                        <span className="ml-2 text-xs text-orange-600">(Basic)</span>
                      )}
                    </Label>
                  </motion.div>
                )
              })}
            </div>
          </div>

          {/* Premium Feeds */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="font-semibold text-gray-900">Premium Feeds</h3>
              <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                {sortedPremiumFeeds.length} Total
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
              {sortedPremiumFeeds.map((feed) => {
                const hasAccess = hasAccessToFeed(feed.alias)
                return (
                  <motion.div
                    key={feed.alias}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${hasAccess
                      ? 'hover:bg-gray-50 border-gray-200'
                      : 'bg-gray-50 border-gray-300 opacity-60'
                      }`}
                    title={!hasAccess ? 'Upgrade to Premium plan to access' : feed.short_description || 'News feed'}
                  >
                    <div className="relative">
                      <Checkbox
                        id={feed.alias}
                        checked={isFeedSelected(feed.alias)}
                        onCheckedChange={() => hasAccess && toggleFeed(feed)}
                        disabled={!hasAccess}
                      />
                      {!hasAccess && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="text-red-600 text-lg font-bold">✖</span>
                        </div>
                      )}
                    </div>
                    <Label
                      htmlFor={feed.alias}
                      title={feed.short_description || 'News feed'}
                      className={`flex-1 text-sm ${hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'
                        }`}
                    >
                      {feed.display_name}
                      {!hasAccess && (
                        <span className="ml-2 text-xs text-red-600">(Premium)</span>
                      )}
                    </Label>
                  </motion.div>
                )
              })}
            </div>
          </div>

          {/* Newsletters Section (New Independent Section) */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="font-semibold text-gray-900">Newsletters</h3>
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                {sortedNewsletterFeeds.length} Total
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
              {sortedNewsletterFeeds.map((feed) => {
                const hasAccess = hasAccessToFeed(feed.alias)

                return (
                  <motion.div
                    key={feed.alias}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${hasAccess
                      ? 'hover:bg-gray-50 border-gray-200'
                      : 'bg-gray-50 border-gray-300 opacity-60'
                      }`}
                    title={!hasAccess ? 'Subscribe to newsletter to enable' : feed.short_description || 'News feed'}
                  >
                    <div className="relative">
                      <Checkbox
                        id={feed.alias}
                        checked={isFeedSelected(feed.alias)}
                        onCheckedChange={() => hasAccess && toggleFeed(feed)}
                        disabled={!hasAccess}
                      />
                      {!hasAccess && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="text-gray-500 text-lg font-bold">✖</span>
                        </div>
                      )}
                    </div>
                    <Label
                      htmlFor={feed.alias}
                      className={`flex-1 text-sm ${hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'
                        }`}
                      title={feed.short_description || 'News feed'}
                    >
                      {feed.display_name}
                      <span className="ml-2 text-xs text-blue-600">(Newsletter)</span>
                    </Label>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* By State - Map View (SVG Interactive) */}
      {sortType === 'byState' && viewType === 'map' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <USAInteractiveMap
              selectedState={selectedState}
              onStateSelect={setSelectedState}
            />
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {selectedState ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">{selectedState}</h3>
                  {getStateFeeds(selectedState).length > 0 && (
                    <Checkbox
                      id="select-all-state"
                      checked={isStateSelected(getStateIdByName(selectedState))}
                      onCheckedChange={() => toggleStateFeeds(getStateIdByName(selectedState))}
                    />
                  )}
                </div>
                {getStateFeeds(selectedState).length > 0 ? (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {/* Basic Feeds (Free + Basic combined) */}
                    {getStateFeeds(selectedState).filter(f => f.non_profit_org || f.basic_feed).length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-primary mb-2">Basic</h4>
                        <div className="space-y-2">
                          {getStateFeeds(selectedState).filter(f => f.non_profit_org || f.basic_feed).map((feed) => {
                            const hasAccess = hasAccessToFeed(feed.alias)
                            const isFreeFeed = feed.non_profit_org
                            return (
                              <div key={feed.alias} className={`flex items-center gap-3 p-3 rounded-lg border ${hasAccess ? 'hover:bg-gray-50 border-gray-200' : 'bg-gray-50 border-gray-300 opacity-60'}`} title={!hasAccess ? 'Upgrade to Basic plan' : feed.short_description || 'News feed'}>
                                <div className="relative">
                                  <Checkbox id={`state-${feed.alias}`} checked={isFeedSelected(feed.alias)} onCheckedChange={() => hasAccess && toggleFeed(feed)} disabled={!hasAccess} />
                                  {!hasAccess && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><span className="text-orange-600 text-lg font-bold">✖</span></div>}
                                </div>
                                <Label htmlFor={`state-${feed.alias}`} className={`flex-1 text-sm ${hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'}`} title={feed.short_description || 'News feed'}>
                                  {feed.display_name}{isFreeFeed ? <span className="ml-2 text-xs text-green-600">(Free)</span> : !hasAccess && <span className="ml-2 text-xs text-orange-600">(Basic)</span>}
                                </Label>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* Premium Feeds */}
                    {getStateFeeds(selectedState).filter(f => !f.non_profit_org && !f.basic_feed).length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-purple-700 mb-2">Premium</h4>
                        <div className="space-y-2">
                          {getStateFeeds(selectedState).filter(f => !f.non_profit_org && !f.basic_feed).map((feed) => {
                            const hasAccess = hasAccessToFeed(feed.alias)
                            return (
                              <div key={feed.alias} className={`flex items-center gap-3 p-3 rounded-lg border ${hasAccess ? 'hover:bg-gray-50 border-gray-200' : 'bg-gray-50 border-gray-300 opacity-60'}`} title={!hasAccess ? 'Upgrade to Premium plan' : feed.short_description || 'News feed'}>
                                <div className="relative">
                                  <Checkbox id={`state-${feed.alias}`} checked={isFeedSelected(feed.alias)} onCheckedChange={() => hasAccess && toggleFeed(feed)} disabled={!hasAccess} />
                                  {!hasAccess && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><span className="text-red-600 text-lg font-bold">✖</span></div>}
                                </div>
                                <Label htmlFor={`state-${feed.alias}`} className={`flex-1 text-sm ${hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'}`} title={feed.short_description || 'News feed'}>
                                  {feed.display_name}{!hasAccess && <span className="ml-2 text-xs text-red-600">(Premium)</span>}
                                </Label>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Rss className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-700 mb-1">No providers available</p>
                    <p className="text-xs text-gray-500">This state has no news feeders yet</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <Rss className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-sm font-medium text-gray-700 mb-1">Select a state</p>
                <p className="text-xs text-gray-500">Click a state on the map to view feeds</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* By State - Grid View (Grid of state buttons) */}
      {sortType === 'byState' && viewType === 'grid' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <USMap
              selectedState={selectedState}
              onStateSelect={setSelectedState}
              feedCounts={feedCounts}
            />
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {selectedState ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">{selectedState}</h3>
                  {getStateFeeds(selectedState).length > 0 && (
                    <Checkbox
                      id="select-all-state-grid"
                      checked={isStateSelected(getStateIdByName(selectedState))}
                      onCheckedChange={() => toggleStateFeeds(getStateIdByName(selectedState))}
                    />
                  )}
                </div>
                {getStateFeeds(selectedState).length > 0 ? (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {/* Basic Feeds in State (Free + Basic combined) */}
                    {getStateFeeds(selectedState).filter(f => f.non_profit_org || f.basic_feed).length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-primary mb-2">Basic</h4>
                        <div className="space-y-2">
                          {getStateFeeds(selectedState).filter(f => f.non_profit_org || f.basic_feed).map((feed) => {
                            const hasAccess = hasAccessToFeed(feed.alias)
                            const isFreeFeed = feed.non_profit_org
                            return (
                              <div
                                key={feed.alias}
                                className={`flex items-center gap-3 p-3 rounded-lg border ${hasAccess ? 'hover:bg-gray-50 border-gray-200' : 'bg-gray-50 border-gray-300 opacity-60'}`}
                                title={!hasAccess ? 'Upgrade subscription to access' : feed.short_description || 'News feed'}
                              >
                                <div className="relative">
                                  <Checkbox
                                    id={`grid-${feed.alias}`}
                                    checked={isFeedSelected(feed.alias)}
                                    onCheckedChange={() => hasAccess && toggleFeed(feed)}
                                    disabled={!hasAccess}
                                  />
                                  {!hasAccess && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <span className="text-orange-600 text-lg font-bold">✖</span>
                                    </div>
                                  )}
                                </div>
                                <Label
                                  htmlFor={`grid-${feed.alias}`}
                                  className={`flex-1 text-sm ${hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'}`}
                                  title={feed.short_description || 'News feed'}
                                >
                                  {feed.display_name}
                                  {isFreeFeed ? <span className="ml-2 text-xs text-green-600">(Free)</span> : !hasAccess && <span className="ml-2 text-xs text-orange-600">(Basic)</span>}
                                </Label>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* Premium Feeds in State */}
                    {getStateFeeds(selectedState).filter(f => !f.non_profit_org && !f.basic_feed).length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-primary mb-2">Premium</h4>
                        <div className="space-y-2">
                          {getStateFeeds(selectedState).filter(f => !f.non_profit_org).map((feed) => {
                            const hasAccess = hasAccessToFeed(feed.alias)
                            return (
                              <div
                                key={feed.alias}
                                className={`flex items-center gap-3 p-3 rounded-lg border ${hasAccess
                                  ? 'hover:bg-gray-50 border-gray-200'
                                  : 'bg-gray-50 border-gray-300 opacity-60'
                                  }`}
                                title={!hasAccess ? 'Upgrade subscription to access' : feed.short_description || 'News feed'}
                              >
                                <div className="relative">
                                  <Checkbox
                                    id={`grid-${feed.alias}`}
                                    checked={isFeedSelected(feed.alias)}
                                    onCheckedChange={() => hasAccess && toggleFeed(feed)}
                                    disabled={!hasAccess}
                                  />
                                  {!hasAccess && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <span className="text-red-600 text-lg font-bold">✖</span>
                                    </div>
                                  )}
                                </div>
                                <Label
                                  htmlFor={`grid-${feed.alias}`}
                                  className={`flex-1 text-sm ${hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'
                                    }`}
                                  title={feed.short_description || 'News feed'}
                                >
                                  {feed.display_name}
                                  {!hasAccess && (
                                    <span className="ml-2 text-xs text-red-600">(Premium)</span>
                                  )}
                                </Label>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Rss className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-700 mb-1">No providers available</p>
                    <p className="text-xs text-gray-500">This state has no news feeders yet</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <Rss className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-sm font-medium text-gray-700 mb-1">Select a state</p>
                <p className="text-xs text-gray-500">Click a state button to view feeds</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* By State - List View */}
      {sortType === 'byState' && viewType === 'list' && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Feeds by State</h3>
          <div className="space-y-6 max-h-96 overflow-y-auto">
            {groupedByState.map((state) => {
              const stateBasicFeeds = state.feeds.filter(f => f.non_profit_org || f.basic_feed)
              const statePremiumFeeds = state.feeds.filter(f => !f.non_profit_org && !f.basic_feed)

              return (
                <div key={state.id} className="border-b border-gray-200 pb-4 last:border-0">
                  <div className="flex items-center gap-3 mb-3">
                    <Checkbox
                      id={`state-${state.id}`}
                      checked={isStateSelected(state.id)}
                      onCheckedChange={() => toggleStateFeeds(state.id)}
                    />
                    <Label
                      htmlFor={`state-${state.id}`}
                      className="font-semibold cursor-pointer"
                    >
                      {state.name} ({state.feeds.length})
                    </Label>
                  </div>

                  <div className="ml-6 space-y-4">
                    {/* Basic Feeds */}
                    {stateBasicFeeds.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-primary mb-2">Basic</h4>
                        <div className="space-y-2">
                          {stateBasicFeeds.map((feed) => {
                            const hasAccess = hasAccessToFeed(feed.alias)
                            const isFreeFeed = feed.non_profit_org
                            return (
                              <div
                                key={feed.alias}
                                className={`flex items-center gap-3 p-2 rounded-lg border ${hasAccess ? 'hover:bg-gray-50 border-gray-200' : 'bg-gray-50 border-gray-300 opacity-60'}`}
                                title={!hasAccess ? 'Upgrade subscription to access' : feed.short_description || 'News feed'}
                              >
                                <div className="relative">
                                  <Checkbox
                                    id={`list-${feed.alias}`}
                                    checked={isFeedSelected(feed.alias)}
                                    onCheckedChange={() => hasAccess && toggleFeed(feed)}
                                    disabled={!hasAccess}
                                  />
                                  {!hasAccess && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <span className="text-orange-600 text-lg font-bold">✖</span>
                                    </div>
                                  )}
                                </div>
                                <Label
                                  htmlFor={`list-${feed.alias}`}
                                  className={`flex-1 text-sm ${hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'}`}
                                  title={feed.short_description || 'News feed'}
                                >
                                  {feed.display_name}
                                  {isFreeFeed ? <span className="ml-2 text-xs text-green-600">(Free)</span> : !hasAccess && <span className="ml-2 text-xs text-orange-600">(Basic)</span>}
                                </Label>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* Premium Feeds */}
                    {statePremiumFeeds.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-primary mb-2">Premium</h4>
                        <div className="space-y-2">
                          {statePremiumFeeds.map((feed) => {
                            const hasAccess = hasAccessToFeed(feed.alias)
                            return (
                              <div
                                key={feed.alias}
                                className={`flex items-center gap-3 p-2 rounded-lg border ${hasAccess
                                  ? 'hover:bg-gray-50 border-gray-200'
                                  : 'bg-gray-50 border-gray-300 opacity-60'
                                  }`}
                                title={!hasAccess ? 'Upgrade subscription to access' : feed.short_description || 'News feed'}
                              >
                                <div className="relative">
                                  <Checkbox
                                    id={`list-${feed.alias}`}
                                    checked={isFeedSelected(feed.alias)}
                                    onCheckedChange={() => hasAccess && toggleFeed(feed)}
                                    disabled={!hasAccess}
                                  />
                                  {!hasAccess && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <span className="text-red-600 text-lg font-bold">✖</span>
                                    </div>
                                  )}
                                </div>
                                <Label
                                  htmlFor={`list-${feed.alias}`}
                                  className={`flex-1 text-sm ${hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'
                                    }`}
                                  title={feed.short_description || 'News feed'}
                                >
                                  {feed.display_name}
                                  {!hasAccess && (
                                    <span className="ml-2 text-xs text-red-600">(Premium)</span>
                                  )}
                                </Label>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* By Interest View */}
      {sortType === 'byInterest' && (
        <div className="space-y-6">
          {groupedByCategory.length === 0 ? (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center">
              <p className="text-gray-600">No feeds with categories available</p>
            </div>
          ) : (
            groupedByCategory.map((category) => (
              <div key={category.slug} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                {/* Category Header */}
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="font-semibold text-gray-900">{category.title}</h3>
                  <span className="px-2 py-1 bg-slate-100 text-primary text-xs font-medium rounded-full">
                    {category.subcategories.reduce((acc, sub) => acc + sub.feeds.length, 0)} Feeds
                  </span>
                </div>

                {/* Subcategories */}
                <div className="space-y-4">
                  {category.subcategories.map((subcategory) => (
                    <div key={subcategory.slug} className="border-l-2 border-gray-200 pl-4">
                      {/* Subcategory Header */}
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-sm font-medium text-gray-700">{subcategory.title}</h4>
                        <span className="text-xs text-gray-500">
                          {subcategory.feeds.length} feed{subcategory.feeds.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {/* Feeds in this subcategory */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {subcategory.feeds.map((feed) => {
                          const hasAccess = hasAccessToFeed(feed.alias)
                          const isFreeFeed = feed.non_profit_org
                          const isBasicFeed = feed.basic_feed

                          return (
                            <motion.div
                              key={feed.alias}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className={`flex items-center gap-3 p-3 rounded-lg border ${hasAccess
                                ? 'hover:bg-gray-50 border-gray-200'
                                : 'bg-gray-50 border-gray-300 opacity-60'
                                }`}
                              title={!hasAccess ? 'Upgrade to access this feed' : feed.short_description || 'News feed'}
                            >
                              <div className="relative">
                                <Checkbox
                                  id={`interest-${feed.alias}`}
                                  checked={isFeedSelected(feed.alias)}
                                  onCheckedChange={() => hasAccess && toggleFeed(feed)}
                                  disabled={!hasAccess}
                                />
                                {!hasAccess && (
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <span className="text-orange-600 text-lg font-bold">✖</span>
                                  </div>
                                )}
                              </div>
                              <Label
                                htmlFor={`interest-${feed.alias}`}
                                className={`flex-1 text-sm ${hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'
                                  }`}
                                title={feed.short_description || 'News feed'}
                              >
                                {feed.display_name}
                                {isFreeFeed ? (
                                  <span className="ml-2 text-xs text-green-600">(Free)</span>
                                ) : isBasicFeed ? (
                                  <span className="ml-2 text-xs text-orange-600">(Basic)</span>
                                ) : !hasAccess && (
                                  <span className="ml-2 text-xs text-red-600">(Premium)</span>
                                )}
                              </Label>
                            </motion.div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}

          {/* Uncategorized feeds (if any) */}
          {allFeeds.some(feed => !feed.is_newsletter && !feed.category) && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="font-semibold text-gray-900">Uncategorized</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {allFeeds
                  .filter(feed => !feed.is_newsletter && !feed.category)
                  .map((feed) => {
                    const hasAccess = hasAccessToFeed(feed.alias)
                    const isFreeFeed = feed.non_profit_org

                    return (
                      <motion.div
                        key={feed.alias}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={`flex items-center gap-3 p-3 rounded-lg border ${hasAccess
                          ? 'hover:bg-gray-50 border-gray-200'
                          : 'bg-gray-50 border-gray-300 opacity-60'
                          }`}
                        title={!hasAccess ? 'Upgrade to access this feed' : feed.short_description || 'News feed'}
                      >
                        <div className="relative">
                          <Checkbox
                            id={`uncat-${feed.alias}`}
                            checked={isFeedSelected(feed.alias)}
                            onCheckedChange={() => hasAccess && toggleFeed(feed)}
                            disabled={!hasAccess}
                          />
                          {!hasAccess && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <span className="text-orange-600 text-lg font-bold">✖</span>
                            </div>
                          )}
                        </div>
                        <Label
                          htmlFor={`uncat-${feed.alias}`}
                          className={`flex-1 text-sm ${hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'
                            }`}
                          title={feed.short_description || 'News feed'}
                        >
                          {feed.display_name}
                          {isFreeFeed ? (
                            <span className="ml-2 text-xs text-green-600">(Free)</span>
                          ) : !hasAccess && (
                            <span className="ml-2 text-xs text-red-600">(Premium)</span>
                          )}
                        </Label>
                      </motion.div>
                    )
                  })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
